import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LanguageIcon from '@mui/icons-material/Language';
import { Backdrop, Box, IconButton, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';

import CustomFooter from '../../../../components/Footer/CustomFooter';
import {
  useCreateUpdateCompanyTechUseMutation,
  useDeleteCompanyTechUseMutation,
  useDeleteSelectedCompanyTechUseMutation,
  useGetStackshareToolCategoriesQuery,
  useGetStackshareToolsLazyQuery,
  useGetTechnologyUseQuery,
  // Existing mutation for the URL
  useAddTechnologyFindingUrlMutation,
  // NEW mutation for the text
  useAddTechnologyFindingTextMutation,
} from '../../../../gql/generated/graphql';
import { getNodes } from '../../../../gql/helpers';
import { handleVisitPage } from '../../../../helpers/helpers';
import { TechnologyCategories } from '../../types';
import TechnologySelect from '../TechnologySelect/TechnologySelect';

import { validateTechnologyStyles } from './styles';

type Props = {
  companyId: number;
};

export type TechnologyType = {
  category: string | null;
  technology: string | null;
  stackshareToolId: number | null;
};

const ValidateTechnology = ({ companyId }: Props) => {
  const { t } = useTranslation();

  // Local state for the URL reference
  const [technReferenceUrl, setTechnReferenceUrl] = useState('');

  // NEW: Local state for the big text to be analyzed
  const [technFindingText, setTechnFindingText] = useState('');

  // Track selected rows in DataGrid
  const [selectedTechIds, setSelectedTechIds] = useState<number[]>([]);

  // Local state for category/technology selections
  const [technology, setTechnology] = useState<TechnologyType>({
    category: null,
    technology: null,
    stackshareToolId: null,
  });

  // Queries
  const {
    data: technologyUsed,
    loading: technologyUsedLoading,
    refetch: technologyUsedRefetch,
  } = useGetTechnologyUseQuery({
    variables: { companyId },
  });
  const { data: stackshareToolCategories, loading: stackshareToolCategoriesLoading } =
    useGetStackshareToolCategoriesQuery();
  const [getStackshareTools, { data: stackshareTools, loading: stackshareToolsLoading }] =
    useGetStackshareToolsLazyQuery();

  // Mutations
  const [deleteTechnology, { loading: deleteTechnologyLoading }] = useDeleteCompanyTechUseMutation();
  const [deleteSelectedTechnologies, { loading: deleteSelectedTechnologiesLoading }] =
    useDeleteSelectedCompanyTechUseMutation();

  const [createUpdateCompanyTechUse, { loading: createUpdateCompanyTechUseLoading }] =
    useCreateUpdateCompanyTechUseMutation();

  // Existing mutation for adding the technology-finding URL
  const [addTechnologyFindingUrl, { loading: addTechnologyFindingUrlLoading }] = useAddTechnologyFindingUrlMutation();

  // NEW: Mutation for adding the technology-finding TEXT
  const [addTechnologyFindingText, { loading: addTechnologyFindingTextLoading }] =
    useAddTechnologyFindingTextMutation();

  // Convert edges to node arrays
  const stackshareToolCategoriesNodes = getNodes(stackshareToolCategories?.getStackshareToolCategories);
  const stackshareToolsNodes = getNodes(stackshareTools?.getStackshareTools);

  // Convert returned tech use edges for the DataGrid
  const technologyUsedNodes = technologyUsed?.getCompany?.edges?.[0]?.node?.techUse?.edges?.map(techUseEdge => {
    const techUseNode = techUseEdge?.node;
    const companyNode = technologyUsed?.getCompany?.edges?.[0]?.node;

    return {
      id: techUseNode?.stackshareTool?.id,
      type: techUseNode?.stackshareTool?.breadcrumb,
      name: techUseNode?.stackshareTool?.name,
      stacksUsed: techUseNode?.stackshareTool?.stacksUsed,
      votes: techUseNode?.stackshareTool?.votes,
      crawlUrl: companyNode?.crawlUrl,
      findingUrl: techUseNode?.findingUrl,
      dbId: techUseNode?.dbId,
    };
  });

  // DataGrid columns
  const technologyColumns = [
    {
      field: 'type',
      headerName: t('validation.validateTechnology.categoryColumnLabel'),
      width: 200,
    },
    {
      field: 'name',
      headerName: t('validation.validateTechnology.toolColumnLabel'),
      width: 200,
    },
    {
      field: 'stacksUsed',
      headerName: t('validation.validateTechnology.stacksUsedColumnLabel'),
      width: 200,
    },
    {
      field: 'votes',
      headerName: t('validation.validateTechnology.votesColumnLabel'),
      width: 100,
    },
    {
      field: 'actions',
      headerName: '',
      width: 200,
      editable: false,
      sortable: false,
      renderCell: params => (
        <>
          <IconButton size="small" onClick={() => handleOpenPage(params?.row?.crawlUrl, params?.row?.findingUrl)}>
            <Tooltip title={t('validation.validateTechnology.rowIconTooltipVisit')}>
              <LanguageIcon fontSize="small" />
            </Tooltip>
          </IconButton>
          <IconButton size="small" onClick={() => handleDelete(params.row)}>
            <Tooltip title={t('validation.validateTechnology.rowIconTooltipDelete')}>
              <DeleteOutlineIcon fontSize="small" />
            </Tooltip>
          </IconButton>
        </>
      ),
    },
  ];

  // Handlers
  const handleOpenPage = (crawlUrl: string, findingUrl: string) => {
    const fullUrl = `${crawlUrl}${findingUrl}`;
    handleVisitPage(fullUrl);
  };

  const handleDelete = async row => {
    const selectedTechnology = technologyUsed?.getCompany?.edges?.[0]?.node?.techUse?.edges?.find(
      node => node?.node?.stackshareTool?.id === row?.id
    );

    if (!selectedTechnology?.node?.dbId) return;

    const result = await deleteTechnology({
      variables: { techUseId: selectedTechnology?.node?.dbId },
    });

    if (result?.data?.deleteCompanyTechUse?.success) {
      await technologyUsedRefetch();
    }
  };

  const handleDeleteSelectedTechnologies = async (techIds: number[]) => {
    try {
      const result = await deleteSelectedTechnologies({
        variables: { techUseIds: techIds },
      });

      if (result?.data?.deleteSelectedCompanyTechUse?.success) {
        await technologyUsedRefetch();
      }
    } catch (e) {
      console.error('Error during deletion of technologies:', e);
    }
  };

  const handleTechnologyChange = async (name: string, value: string | null) => {
    if (!value) return;

    const selectedTechnology = stackshareToolsNodes.find(item => item?.name === value);

    setTechnology((prevState: TechnologyType) => ({
      ...prevState,
      [name]: value,
      stackshareToolId: selectedTechnology?.dbId,
    }));

    if (name === TechnologyCategories.CATEGORY) {
      await getStackshareTools({ variables: { category: value } });
      setTechnology(prevState => ({
        ...prevState,
        technology: null,
      }));
    }
  };

  const handleChangeTechReferenceUrl = (url: string) => {
    setTechnReferenceUrl(url);
  };

  // NEW: Handle changes for the multiline text
  const handleChangeTechFindingText = (text: string) => {
    setTechnFindingText(text);
  };

  // Add a technology to the table (existing)
  const handleAddTechnology = async () => {
    const result = await createUpdateCompanyTechUse({
      variables: {
        companyId,
        stackshareToolId: technology.stackshareToolId,
      },
    });

    if (result?.data?.createUpdateCompanyTechUse?.companyId) {
      setTechnology({
        category: null,
        technology: null,
        stackshareToolId: null,
      });
      await technologyUsedRefetch();
    }
  };

  // Existing: Add a technology reference URL
  const handleAddTechnologyFindingUrl = async () => {
    try {
      if (!technReferenceUrl) return;

      const result = await addTechnologyFindingUrl({
        variables: {
          companyId,
          findingUrl: technReferenceUrl,
        },
      });

      // If successful, refresh the data after 30 seconds
      if (result?.data?.addTechnologyFindingUrl?.findingUrl) {
        setTimeout(() => {
          technologyUsedRefetch();
        }, 30000);
      }
    } catch (error) {
      console.error('Error adding technology finding URL:', error);
    }
  };

  // NEW: Add a technology finding text
  const handleAddTechnologyFindingText = async () => {
    console.log('handleAddTechnologyFindingText called with:', { companyId, technFindingText });

    try {
      if (!technFindingText) {
        console.warn('No text provided for technology finding');
        return; // or handle validations
      }

      console.log('Calling addTechnologyFindingText mutation...');
      const result = await addTechnologyFindingText({
        variables: {
          companyId,
          findingText: technFindingText,
        },
      });

      console.log('Mutation result:', result);

      // If successful, refresh data after 30 seconds
      if (result?.data?.addTechnologyFindingText?.findingText) {
        console.log('Mutation successful, will refresh in 30 seconds');
        setTimeout(() => {
          technologyUsedRefetch();
        }, 30000);
      }
    } catch (error) {
      console.error('Error adding technology finding text:', error);
    }
  };

  return (
    <Box display="flex" flex={1} flexDirection="column">
      {/* --- DataGrid for existing technologies --- */}
      <DataGrid
        sx={validateTechnologyStyles.dataGrid}
        disableRowSelectionOnClick
        rows={technologyUsedNodes || []}
        columns={technologyColumns}
        loading={technologyUsedLoading}
        rowSelectionModel={selectedTechIds}
        getRowId={row => row?.dbId}
        onRowSelectionModelChange={(ids: number[]) => setSelectedTechIds(ids)}
        rowHeight={35}
        initialState={{
          pagination: {
            paginationModel: {
              pageSize: 10,
            },
          },
        }}
        pageSizeOptions={[10]}
        checkboxSelection
        slots={{
          footer: () => (
            <CustomFooter
              onRefresh={() => technologyUsedRefetch()}
              onDeleteSelected={() => handleDeleteSelectedTechnologies(selectedTechIds)}
            />
          ),
        }}
      />

      {/* --- The TechnologySelect component --- */}
      <TechnologySelect
        categories={stackshareToolCategoriesNodes || []}
        technologies={stackshareToolsNodes || []}
        technReferenceUrl={technReferenceUrl}
        technFindingText={technFindingText}
        state={technology}
        onChangeTechnology={handleTechnologyChange}
        onChangeTechReferenceUrl={handleChangeTechReferenceUrl}
        onChangeTechFindingText={handleChangeTechFindingText}
        onHandleAddTechnology={handleAddTechnology}
        onHandleAddTechnologyFindingUrl={handleAddTechnologyFindingUrl}
        onHandleAddTechnologyFindingText={handleAddTechnologyFindingText}
        technologiesLoading={stackshareToolsLoading}
        categoriesLoading={stackshareToolCategoriesLoading}
      />

      {/* --- Loading overlay if any mutation is in progress --- */}
      <Backdrop
        open={
          deleteTechnologyLoading ||
          deleteSelectedTechnologiesLoading ||
          createUpdateCompanyTechUseLoading ||
          addTechnologyFindingUrlLoading ||
          addTechnologyFindingTextLoading
        }
      />
    </Box>
  );
};

export default ValidateTechnology;
